import { FC, memo, useCallback, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';

import { css, cx } from '@emotion/css';
import copy from 'clipboard-copy';

import {
    AccountName,
    AccountTitle,
    Avatar,
    FlexColumn,
    FlexRow,
    Image2,
    LinkContainer,
    SideMenuLink,
    Small,
} from '@portal/common/components';
import { InjectedMediaProps, styled, withMedia } from '@portal/common/theme';
import { OrganizationMember } from '@portal/common/types';
import getAppVersionText from '@portal/common/utility/get-app-version-text';

import _ from '../../locale';
import {
    CATEGORIES_PATH,
    CONTENT_MANAGEMENT_PATH,
    CONTENT_MANAGEMENT_WELCOME_MESSAGE_PATH,
    DATA_COLLECTIONS_PATH,
    FILES_PATH,
    HOME_PATH,
    INSIGHTS_PATH,
    LINKS_PATH,
    ORGANIZATIONS_PATH,
    PROFILE_PATH,
    SETTINGS_SMART_OPTIONS_PATH,
    STAFF_PATH,
    SURVEYS_PATH,
} from '../../router/paths';

const Container = styled('div')(({ theme }) => ({
    display: 'flex',
    flexFlow: 'row nowrap',
}));

const Sidenav = styled('div')(({ theme }) => ({
    color: '#fff',
    background: '#173F5F',
    display: 'flex',
    flexFlow: 'column nowrap',
    width: 270,
    minWidth: 270,
}));

const SidenavGroup = styled('div')(({ theme }) => ({
    flexShrink: 0,
    display: 'flex',
    flexFlow: 'column nowrap',
    padding: '0px 30px 30px 30px',
}));

const SidenavMainMenu = styled('div')(({ theme }) => ({
    flexGrow: 1,
    display: 'block',
    padding: '0 30px 30px 35px',
    overflow: 'auto',
}));

const SidenavMenuGroupTitle = styled(Small.withComponent('b'))(({ theme }) => ({
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeight.heavy,
    fontSize: '1.2rem',
    color: theme.color.white30,
}));

const LogoContainer = styled(SidenavGroup)({
    paddingTop: 30,
    height: 90,
    img: {
        height: 30,
    },
});

const StyledAvatar = styled(Avatar)({
    marginRight: 10,
    cursor: 'pointer',
});

const VersionText = styled('div')(({ theme }) => ({
    fontSize: 14,
    color: theme.color.white30,
    cursor: 'pointer',
    padding: '10px 0',
    textAlign: 'center',
}));

const accountNameStyles = cx(css`
    width: 160px;
`);

const accountTitleStyles = cx(css`
    width: 160px;
    color: #ffffff50 !important;
`);

type Props = InjectedMediaProps & {
    account: OrganizationMember | null;
};

const View: FC<Props> = memo(({ account = null, matchMedia }) => {
    const location = useLocation();
    const checkIsContentManagementLinkActive = useCallback(() => {
        const { pathname } = location;
        return pathname.includes(CONTENT_MANAGEMENT_PATH);
    }, [location]);

    const versionText = useMemo(() => getAppVersionText('Staff Portal', config.env), []);

    const handleClickOnVersionText = useCallback(() => {
        copy(versionText);
    }, [versionText]);

    if (!account) return null;

    const logoSrc = matchMedia({
        mobile: '/img/logo-ihme-staff-portal.png',
        tablet: '/img/logo-ihme-staff-portal.png',
    });

    return (
        <Container>
            <Sidenav>
                <LogoContainer>
                    <RouterLink to="/">
                        <Image2 src={logoSrc} />
                    </RouterLink>
                </LogoContainer>

                <SidenavGroup>
                    <LinkContainer to={PROFILE_PATH}>
                        <FlexRow>
                            <FlexColumn>
                                <StyledAvatar src={account.avatar_url} />
                            </FlexColumn>
                            <FlexColumn justify="center" itemsSpacing={4}>
                                <AccountName className={accountNameStyles} account={account} />
                                <AccountTitle
                                    className={accountTitleStyles}
                                    title={account.title}
                                />
                            </FlexColumn>
                        </FlexRow>
                    </LinkContainer>
                </SidenavGroup>

                <SidenavMainMenu>
                    <FlexColumn itemsSpacing={30}>
                        <FlexColumn itemsSpacing={5}>
                            <SidenavMenuGroupTitle>{_.get('nav_group_main')}</SidenavMenuGroupTitle>
                            <FlexColumn>
                                <LinkContainer to={HOME_PATH}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-activity.svg"
                                        beforeIconAlt={_.get('nav_activity')}
                                    >
                                        <div>{_.get('nav_activity')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                                <LinkContainer to={INSIGHTS_PATH} end={false}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-barchart.svg"
                                        beforeIconAlt={_.get('nav_insights')}
                                    >
                                        <div>{_.get('nav_insights')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                            </FlexColumn>
                        </FlexColumn>

                        <FlexColumn itemsSpacing={5}>
                            <SidenavMenuGroupTitle>
                                {_.get('nav_group_content')}
                            </SidenavMenuGroupTitle>
                            <FlexColumn>
                                <LinkContainer to={CATEGORIES_PATH}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-folder.svg"
                                        beforeIconAlt={_.get('nav_categories')}
                                    >
                                        <div>{_.get('nav_categories')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                                <LinkContainer to={LINKS_PATH}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-link.svg"
                                        beforeIconAlt={_.get('nav_links')}
                                    >
                                        <div>{_.get('nav_links')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                                <LinkContainer to={FILES_PATH}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-file.svg"
                                        beforeIconAlt={_.get('nav_files')}
                                    >
                                        <div>{_.get('nav_files')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                                <LinkContainer to={DATA_COLLECTIONS_PATH} end={false}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-switches.svg"
                                        beforeIconAlt={_.get('nav_data_collections')}
                                    >
                                        <div>{_.get('nav_data_collections')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                                <LinkContainer
                                    to={CONTENT_MANAGEMENT_WELCOME_MESSAGE_PATH}
                                    checkIsActive={checkIsContentManagementLinkActive}
                                >
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-talkbox.svg"
                                        beforeIconAlt={_.get('nav_content_management')}
                                    >
                                        <div>{_.get('nav_content_management')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                                <LinkContainer to={SURVEYS_PATH} end={false}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-survey.svg"
                                        beforeIconAlt={_.get('nav_surveys')}
                                    >
                                        <div>{_.get('nav_surveys')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                            </FlexColumn>
                        </FlexColumn>

                        <FlexColumn itemsSpacing={5}>
                            <SidenavMenuGroupTitle>
                                {_.get('nav_group_clients')}
                            </SidenavMenuGroupTitle>
                            <FlexColumn>
                                <LinkContainer to={ORGANIZATIONS_PATH} end={false}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-team.svg"
                                        beforeIconAlt={_.get('nav_organizations')}
                                    >
                                        <div>{_.get('nav_organizations')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                            </FlexColumn>
                        </FlexColumn>

                        <FlexColumn itemsSpacing={5}>
                            <SidenavMenuGroupTitle>{_.get('nav_group_ihme')}</SidenavMenuGroupTitle>
                            <FlexColumn>
                                <LinkContainer to={STAFF_PATH}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-lock.svg"
                                        beforeIconAlt={_.get('nav_staff')}
                                    >
                                        <div>{_.get('nav_staff')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                                <LinkContainer to={SETTINGS_SMART_OPTIONS_PATH}>
                                    <SideMenuLink
                                        beforeIconSrc="/icons/ic-settings.svg"
                                        beforeIconAlt={_.get('nav_settings')}
                                    >
                                        <div>{_.get('nav_settings')}</div>
                                    </SideMenuLink>
                                </LinkContainer>
                            </FlexColumn>
                        </FlexColumn>
                    </FlexColumn>
                </SidenavMainMenu>
                <VersionText onClick={handleClickOnVersionText}>{versionText}</VersionText>
            </Sidenav>
        </Container>
    );
});

export default withMedia(View);
